import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import {
  ComplianceData,
  ComplianceDeclaration,
  ComplianceExemption,
  ComplianceProduct,
  GridOverrides,
  RowSelection
} from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ButtonComponent, ConfirmationDialogComponent, getFieldType, GridActionsComponent, GridModule } from '@mds/ui';
import { FormlyModule } from '@ngx-formly/core';
import { ComposerApiService } from '@services/composer-api.service';
import { ToastrService } from '@services/toaster.service';
import { ColDef, GridApi, RowNode } from 'ag-grid-enterprise';
import { forkJoin, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ComplianceQuestionnaireCommentsComponent } from '../compliance-questionnaire-comments/compliance-questionnaire-comments.component';

enum StatementStatus {
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non-compliant',
  COMPLIANT_WITH_EXEMPTION = 'compliant-with-exemption'
}

@Component({
  selector: 'app-compliance-questionnaire-edit',
  templateUrl: './compliance-questionnaire-edit.component.html',
  styleUrls: ['./compliance-questionnaire-edit.component.scss'],
  imports: [
    CommonModule,
    MdsIconsModule,
    GridModule,
    FormlyModule,
    MatInputModule,
    ButtonComponent,
    MatTooltipModule,
    MatIconModule,
    MatDialogModule,
    ReactiveFormsModule,
    ComplianceQuestionnaireCommentsComponent
  ],
  standalone: true
})
export class ComplianceQuestionnaireEditComponent implements OnInit, OnDestroy {
  mdsIcon = MdsIcon;
  statementForm = new FormGroup({
    statement: new FormControl(''),
    scip_primary_article_identifier_value: new FormControl('')
  });
  data: ComplianceData = inject(MAT_DIALOG_DATA);
  statementTypes = [
    {
      key: 'statement',
      defaultValue: this.data?.declaration?.statement ?? '',
      type: getFieldType('statement', 'STRING', 'select'),
      templateOptions: {
        label: 'Statement',
        info: '',
        placeholder: 'RSL Statement',
        disabled: !this.data.editable,
        suffix: '',
        prefix: '',
        type: 'STRING',
        required: true,
        options: this.data.selectedRsl.declarations?.map((declaration) => ({
          name: declaration.statement,
          value: declaration.statement,
          ...declaration
        })),
        vertical: true
      }
    },
    {
      key: 'scip_primary_article_identifier_value',
      defaultValue:
        this.data.selectedProducts.length < 2
          ? this.data.selectedProducts[0].scip_primary_article_identifier_value
          : '',
      type: getFieldType('scip_primary_article_identifier_value', 'STRING', 'input'),
      templateOptions: {
        label: this.data.selectedRsl.formSchema.scip_primary_article_identifier_value?.title || '',
        info: this.data.selectedRsl.formSchema.scip_primary_article_identifier_value?.description || '',
        placeholder: 'XX-XXXX-XX',
        disabled: !this.data.editable,
        suffix: '',
        prefix: '',
        type: 'STRING',
        required: false,
        options: [],
        vertical: true
      },
      hideExpression: true
    }
  ];
  selectedProductsOverrides: GridOverrides;
  exemptionOverrides: GridOverrides;
  currentStatement: StatementStatus;
  previousStatement: string;
  statementStatus = StatementStatus;
  deletedAttachments = [];
  viewedAttachments = new Set<string>();
  gridApi: GridApi;
  showWarning: boolean;
  showAttachments: boolean;
  attachments = [];
  confirmationDialog: MatDialogRef<ConfirmationDialogComponent>;
  isSubstanceRequired = true;
  isReasonRequired = false;
  isAttachmentRequired = false;

  mixedStatement = { name: 'Mixed Statement', value: '' };
  submitDataEmitter = new EventEmitter();
  @ViewChild('confirmationActions') confirmationActions: TemplateRef<HTMLAllCollection>;
  private destroy$ = new Subject<void>();
  comment = '';
  constructor(
    private dialogRef: MatDialogRef<ComplianceQuestionnaireEditComponent>,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private composerApiService: ComposerApiService
  ) {}

  get disabled() {
    return (
      !this.data.editable ||
      !this.currentStatement ||
      (this.isAttachmentRequired && !this.attachments.length && !this.data?.attachments?.length) ||
      ([StatementStatus.NON_COMPLIANT, StatementStatus.COMPLIANT_WITH_EXEMPTION].includes(this.currentStatement) &&
        this.isSubstanceRequired &&
        !this.gridApi?.getRenderedNodes()?.length) ||
      !this.isSCIPInfoProvided
    );
  }

  get isSCIPInfoProvided() {
    if (!this.data?.selectedRsl?.scip_number_required) return true;

    // Not required if statement is Non-Compliant
    if ([StatementStatus.COMPLIANT, StatementStatus.COMPLIANT_WITH_EXEMPTION].includes(this.currentStatement))
      return true;

    // if SCIP number is provided
    if (this.statementForm.controls.scip_primary_article_identifier_value?.value?.length) return true;

    const nodes = [];
    this.gridApi?.forEachNode((node) => {
      nodes.push(node.data);
    });
    // If all substance have Material Category or Mixture Category
    if (nodes.every((node) => node.scip_material_category || node.scip_mixture_category)) return true;

    return false;
  }

  ngOnInit(): void {
    if (this.data.selectedProducts.length > 1) {
      this.createProductsOverrides();
    }

    this.statementForm.controls.statement.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((statement: string) => {
      if (this.showWarning) {
        this.showStatementChangeConfirmation(statement);
      } else {
        this.checkStatementStatus(statement);
        this.previousStatement = statement;
        this.showAttachments = this.data.editable ?? !!statement;
      }
    });
  }

  onSave() {
    const payload = {};
    const statementForm = this.statementForm.getRawValue();
    const selectedDeclaration = this.data.selectedRsl.declarations.find(
      (declaration: ComplianceDeclaration) => declaration.statement === statementForm.statement
    );

    this.data.selectedProducts.forEach((product: ComplianceProduct) => {
      const skuRid = product.rid;
      payload[skuRid] = this.buildPayloadBase(selectedDeclaration);

      if ([StatementStatus.COMPLIANT_WITH_EXEMPTION, StatementStatus.NON_COMPLIANT].includes(this.currentStatement)) {
        this.addCompliantWithExemptionData(payload[skuRid]);
      }
    });

    // if any of the product has SCIP number saved, reset when overriding as COMPLIANT
    if (this.data.selectedProducts.find((product) => product.scip_primary_article_identifier_value)) {
      if (this.currentStatement === StatementStatus.COMPLIANT) {
        Object.values(payload).forEach((sku: Record<string, any>) => {
          sku.scip_primary_article_identifier_value = null;
        });
      }
    }
    this.submitDataEmitter.emit({
      payload,
      attachments: this.attachments,
      deletedAttachments: this.deletedAttachments,
      comment: this.comment || ''
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onRowRemove({ node }) {
    this.gridApi.applyTransaction({ remove: [node.data] });
  }

  addNewRow() {
    if ([StatementStatus.COMPLIANT_WITH_EXEMPTION, StatementStatus.NON_COMPLIANT].includes(this.currentStatement)) {
      this.newExemptionRow();
    }
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
  }

  onFilesDrop(event: any) {
    event.preventDefault();
    this.attachments = [...this.attachments, ...event.dataTransfer.files];
  }

  onFilesUploaded(event: Event) {
    const { files } = event.target as HTMLInputElement;
    this.attachments = [...this.attachments, ...Object.values(files)];
  }

  onAttachmentDownload(index: number) {
    const { link } = this.data.attachments[index];
    if (!link) return;

    const downloadElement = document.createElement('a');
    downloadElement.setAttribute('target', '_blank');
    downloadElement.setAttribute('href', link);
    document.body.appendChild(downloadElement);
    downloadElement.click();
    downloadElement.remove();
  }

  onAttachmentOpenInNewTab(index: number) {
    const attachment = this.data.attachments[index];
    const url = attachment.preview_link || attachment.link;
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
    this.viewedAttachments.add(attachment.rid);
  }

  onAttachmentRemove(index: number, isLocalUpload: boolean) {
    if (isLocalUpload) {
      this.attachments.splice(index, 1);
    } else {
      this.deletedAttachments.push(this.data.attachments[index].rid);
      this.data.attachments.splice(index, 1);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildPayloadBase(selectedDeclaration: ComplianceDeclaration) {
    return {
      out_l_declaration: [
        {
          in: this.data.selectedRsl?.rid ?? '',
          ...selectedDeclaration,
          rid: this.data.declaration?.edge_rid ?? '' // for updates on already created declaration
        }
      ],
      out_l_exemption: [],
      delete_existing_attachments: this.data.selectedProducts.length > 1 // bulk edit pass this flag to clear old files to BE
    };
  }

  private addCompliantWithExemptionData(payload) {
    if (
      this.statementTypes[1].hideExpression === false &&
      this.statementForm.getRawValue().scip_primary_article_identifier_value
    ) {
      payload.scip_primary_article_identifier_value =
        this.statementForm.getRawValue().scip_primary_article_identifier_value;
    }
    this.gridApi.forEachNode((node: RowNode) => {
      const {
        reason,
        substance,
        child_substance,
        max_proportion,
        stype,
        status,
        limits,
        exemption_limit,
        notes,
        scip_material_category,
        scip_mixture_category
      } = node.data;
      payload.out_l_exemption.push({
        in: this.data.selectedRsl.rid,
        reason,
        substance,
        child_substance,
        max_proportion,
        stype,
        status,
        limits,
        exemption_limit,
        notes,
        scip_material_category,
        scip_mixture_category
      });
    });
  }

  private newExemptionRow() {
    const formSchema: any = this.data.selectedRsl.formSchema.out_l_exemption;
    formSchema
      .filter((field: any) => field.key === 'out_l_exemption.in')
      .forEach((field: any) => {
        field.defaultValue = this.data.selectedRsl.rid;
        field.formtype = 'display_only';
      });
    formSchema.filter((field: any) => field.key === 'out_l_exemption.reason')[0].mandatory = this.isReasonRequired;
    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: {
        formSchema,
        lines: []
      }
    });

    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter.pipe(takeUntil(this.destroy$)).subscribe(({ valid, value }) => {
      if (!valid) return;
      const res = value.Inputs.out_l_exemption;

      const tilesDataObservable = [
        res.substance,
        res.child_substance,
        res.scip_material_category,
        res.scip_mixture_category
      ];
      forkJoin(
        tilesDataObservable.map((rid: string) => {
          if (rid) return this.composerApiService.getTileCards(rid);
          return of([]);
        })
      )
        .pipe(
          takeUntil(this.destroy$),
          map((data: any[]) => data.map((cards) => cards[0]))
        )
        .subscribe((data: any[]) => {
          const [substance, child_substance, scip_material_category, scip_mixture_category] = data;
          this.gridApi.applyTransaction({
            add: [
              {
                actions: { delete: true },
                ...res,
                substance_name: substance?.title,
                secondary_substance_name: child_substance?.title,
                scip_material_category_name: scip_material_category?.title,
                scip_mixture_category_name: scip_mixture_category?.title,
                substance_rid: substance?.rid
              }
            ]
          });
        });
      dialogRef.close();
    });
  }

  private checkStatementStatus(statement: string) {
    const statementObj = this.data.selectedRsl.declarations?.find(
      (declaration: ComplianceDeclaration) => declaration.statement === statement
    );
    this.currentStatement = StatementStatus.COMPLIANT;
    if (statementObj.substance_required) {
      // show exemption table
      this.currentStatement = statementObj.status
        ? StatementStatus.COMPLIANT_WITH_EXEMPTION
        : StatementStatus.NON_COMPLIANT;
      this.createExemptionOverrides();
      this.statementTypes[1].hideExpression = !this.data.selectedRsl.formSchema.scip_primary_article_identifier_value;
    }

    this.isReasonRequired = statementObj.exemption_required ?? false;
    this.isSubstanceRequired = statementObj.substance_required ?? true;
    this.isAttachmentRequired = statementObj.needs_attachment ?? false;

    // SCIP number required Only when Product is Not Compliant
    if ([StatementStatus.COMPLIANT, StatementStatus.COMPLIANT_WITH_EXEMPTION].includes(this.currentStatement)) {
      this.statementTypes[1].hideExpression = true;
      this.statementForm.controls.scip_primary_article_identifier_value?.reset();
    }
  }

  // different statement confirmation
  private showStatementChangeConfirmation(statement: string) {
    this.confirmationDialog = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Override Compliance Statements',
        content: `You have changed the statement field, which will override your previous settings for all selected products (${this.data.selectedProducts.length}). Would you like to proceed?`,
        actions: this.confirmationActions
      },
      width: '544px'
    });
    this.confirmationDialog.afterClosed().subscribe((override: boolean) => {
      if (override) {
        this.toastrService.showMessage('Your settings have been saved', 1000);
        this.showWarning = false;
        this.data.sku_exemptions = [];
        this.confirmationDialog.close();
        this.checkStatementStatus(statement);
        this.showAttachments = this.data.editable ?? !!statement;
      } else {
        this.statementForm.get('statement')?.setValue(this.previousStatement, { emitEvent: false });
      }
    });
  }

  // grid overrides
  private createExemptionOverrides() {
    // BE sends different payloads for updated and new data. refactor when fixed.
    const rowData = this.data?.sku_exemptions.length
      ? this.data?.sku_exemptions.map((exemption: ComplianceExemption) => ({
          actions: { delete: this.data.editable }, // Include actions like delete
          substance: exemption.substance,
          substance_rid: exemption.substance,
          substance_name: exemption.substance_name ?? '',
          child_substance: exemption.child_substance,
          secondary_substance_name: exemption.child_substance_name,
          reason: exemption.reason,
          notes: exemption.notes,
          max_proportion: exemption.max_proportion ?? '',
          scip_material_category: exemption.scip_material_category,
          scip_material_category_name: exemption.scip_material_category_name,
          scip_mixture_category: exemption.scip_mixture_category,
          scip_mixture_category_name: exemption.scip_mixture_category_name
        }))
      : [];

    this.exemptionOverrides = {
      columnDefs: this.getExemptionColumns(),
      rowData,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        editable: false,
        resizable: true
      },
      groupDefaultExpanded: 1,
      groupSelectsChildren: false,
      suppressRowClickSelection: false,
      suppressAggFuncInHeader: false,
      rowSelection: RowSelection.SINGLE,
      pagination: false,
      paginationAutoPageSize: true
    };
  }

  private getExemptionColumns() {
    const columns: ColDef[] = [
      {
        headerName: 'Substance / Group',
        field: 'substance_name',
        tooltipField: 'substance_name',
        headerTooltip: 'Substance / Group',
        minWidth: 200
      },
      {
        headerName: 'Secondary Substance / Group',
        field: 'secondary_substance_name',
        tooltipField: 'secondary_substance_name',
        headerTooltip: 'Secondary Substance / Group',
        minWidth: 220
      },
      {
        headerName: 'Reason',
        field: 'reason',
        tooltipField: 'reason',
        headerTooltip: 'Reason'
      },
      {
        headerName: 'Concentration',
        field: 'max_proportion',
        tooltipField: 'max_proportion',
        headerTooltip: 'Concentration',
        minWidth: 130
      },
      {
        headerName: 'Notes',
        field: 'notes',
        tooltipField: 'notes',
        headerTooltip: 'Notes'
      },
      {
        headerName: 'SCIP Material Category',
        field: 'scip_material_category_name',
        tooltipField: 'scip_material_category_name',
        headerTooltip: 'SCIP Material Category',
        minWidth: 200
      },
      {
        headerName: 'SCIP Mixture Category',
        field: 'scip_mixture_category_name',
        tooltipField: 'scip_mixture_category_name',
        headerTooltip: 'SCIP Mixture Category',
        minWidth: 200
      },
      {
        headerName: 'Actions',
        field: 'substance_rid',
        lockPosition: true,
        pinned: 'right',
        minWidth: 100,
        cellRenderer: GridActionsComponent,
        cellRendererParams: {
          label: 'Remove',
          onClick: (params: { event: string; node: RowNode }) => {
            this.onRowRemove(params);
          }
        }
      }
    ];
    return columns;
  }

  private createProductsOverrides() {
    this.selectedProductsOverrides = {
      columnDefs: this.getProductColumns(),
      rowData: this.data.selectedProducts,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        resizable: true
      },
      groupDefaultExpanded: 1,
      groupSelectsChildren: false,
      suppressRowClickSelection: false,
      suppressAggFuncInHeader: false,
      pagination: false,
      paginationAutoPageSize: true
    };
  }

  private getProductColumns() {
    const columns: ColDef[] = [
      {
        headerName: 'Product',
        field: 'name',
        tooltipField: 'name',
        headerTooltip: 'Product'
      },
      {
        headerName: 'MPN',
        field: 'prop_MPN',
        tooltipField: 'prop_MPN',
        headerTooltip: 'MPN'
      },
      {
        headerName: 'Part Number',
        field: 'prop_PN',
        tooltipField: 'prop_PN',
        headerTooltip: 'Part Number'
      }
    ];
    const rslKey = this.data.selectedRsl.key;
    columns.push({
      headerName: 'Statement',
      field: rslKey,
      tooltipField: rslKey,
      headerTooltip: 'Statement'
    });
    return columns;
  }
  changeComment(comment: string) {
    this.comment = comment;
  }
}
